import { useSuspenseInfiniteQuery } from '@/hooks/use-suspense-infinite-queries';
import { useSuspenseQuery } from '@/hooks/use-suspense-query';

import { ingredientsApi } from './ingredients-api';

/**
 * Ingredients query hooks using React Query
 */

export function useIngredients(tenantId: string) {
  const { data, ...rest } = useSuspenseQuery(
    ingredientsApi.getOrganisationIngredients(tenantId),
  );

  return {
    data: data ?? [],
    ...rest,
  };
}

export function useIngredient(ingredientId: string, tenantId: string | null) {
  const { data, ...rest } = useSuspenseQuery(
    ingredientsApi.getIngredientById(ingredientId, tenantId),
  );

  return {
    data: data,
    ...rest,
  };
}

/**
 * Hook to search and paginate ingredients for a specific managed supplier with infinite scroll
 * Supports server-side search and pagination for large datasets
 */
export function useManagedSupplierIngredientsInfiniteSearch(
  tenantId: string,
  supplierId: string,
  searchQuery: string,
  options: {
    pageSize?: number;
    enabled?: boolean;
  } = {},
) {
  return useSuspenseInfiniteQuery(
    ingredientsApi.getManagedSupplierIngredientsInfiniteSearch(
      tenantId,
      supplierId,
      searchQuery,
    ),
    {
      ...options,
    },
  );
}

/**
 * Hook to get all nutrient updates for an ingredient
 */
export function useIngredientNutrients(ingredientId: string) {
  const { data, ...rest } = useSuspenseQuery(
    ingredientsApi.getIngredientNutrients(ingredientId),
  );

  return {
    data: data ?? [],
    ...rest,
  };
}

/**
 * Hook to get all allergen updates for an ingredient
 *
 */
export function useIngredientAllergens(ingredientId: string) {
  const { data, ...rest } = useSuspenseQuery(
    ingredientsApi.getIngredientAllergens(ingredientId),
  );

  return {
    data: data ?? [],
    ...rest,
  };
}

/**
 * Hook to get ingredients for a supplier
 */
export function useSupplierIngredients(supplierId: string) {
  const { data, ...rest } = useSuspenseQuery(
    ingredientsApi.getSupplierIngredients(supplierId),
  );

  return {
    data: data ?? [],
    ...rest,
  };
}

/**
 * Hook to get recipes that use a specific ingredient
 */
export function useIngredientRecipes(
  ingredientId: string,
  organisationId: string,
) {
  const { data, ...rest } = useSuspenseQuery(
    ingredientsApi.getIngredientRecipes(ingredientId, organisationId),
  );

  return {
    data: data ?? [],
    ...rest,
  };
}
